import type { DashboardScreeningCounter, TextKey, Translated } from "@repo/shared/types";
import { ScanSearch } from "lucide-react";
import { Link } from "@/components/ui/link";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./screening-widget.i18n";
import { TONE_TINT, type Tone } from "./tone";
import { WidgetCard } from "./widget-card";

type Label = TextKey<Translated<typeof T>>;

const COUNTER: Record<DashboardScreeningCounter["status"], { tone: Tone; label: Label }> = {
  needs_review: { tone: "destructive", label: "needsReview" },
  incomplete: { tone: "warning", label: "incomplete" },
  failed: { tone: "muted", label: "failed" },
  not_checked: { tone: "muted", label: "notChecked" },
};

export function ScreeningWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("screening", target);
  const t = useT(T);
  const { count } = useDashboardFormat();

  return (
    <WidgetCard
      icon={ScanSearch}
      title={t.screening}
      description={t.screeningDescription}
      footer={{ label: t.openClientsReview, to: "/compliance/clients" }}
      isLoading={isLoading}
      error={error}
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          {data.counters.map((counter) => (
            <Link
              key={counter.status}
              to={counter.link}
              className={cn(
                // Tinted panel, foreground text — coloured text on its own tint is too faint.
                "space-y-0.5 rounded-lg p-3 text-foreground transition-opacity hover:opacity-80",
                TONE_TINT[COUNTER[counter.status].tone],
              )}
            >
              <p className="text-2xl font-bold text-foreground">{count(counter.count)}</p>
              <p className="text-sm text-foreground">{t[COUNTER[counter.status].label]}</p>
            </Link>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
